import { API } from '@/config/api';
import { ApiError } from '@/shared/api/ApiError';
import { apiClient } from '@/shared/api/apiClient';

import type {
  GeneratedSuggestions,
  LinkedInAgentMode,
  LinkedInConfiguration,
  LinkedInConfigurationEditRequest,
  LinkedInConfigurationFinding,
  LinkedInConfigurationHistoryEntry,
  LinkedInConfigurationRequest,
  LinkedInConfigurationSections,
  LinkedInConfigurationStatus,
  LinkedInConfigSection,
  ScrapedCompanySummary,
} from '../types';

/**
 * A generation is one model call over data the agent already holds. Generous anyway, as before: it
 * is still a real model call, and the shared client's default would cut it short.
 */
const SUGGEST_TIMEOUT_MS = 90_000;

/** A step as the server sends it: an object whose values may be anything. Only text survives, as on the server. */
function asSection(raw: unknown): LinkedInConfigSection {
  const section: LinkedInConfigSection = {};
  if (raw && typeof raw === 'object') {
    for (const [key, value] of Object.entries(raw)) {
      if (typeof value === 'string') section[key] = value;
    }
  }
  return section;
}

function asSections(
  raw: Partial<Record<keyof LinkedInConfigurationSections, unknown>>,
): LinkedInConfigurationSections {
  return {
    aboutYou: asSection(raw.aboutYou),
    yourCustomers: asSection(raw.yourCustomers),
    yourMessaging: asSection(raw.yourMessaging),
    launchGoals: asSection(raw.launchGoals),
  };
}

interface ConfigurationBody {
  id: string;
  status: LinkedInConfigurationStatus;
  agentMode: LinkedInAgentMode | null;
  suggestionsGenerated: boolean;
  aboutYou: unknown;
  yourCustomers: unknown;
  yourMessaging: unknown;
  launchGoals: unknown;
  createdAt?: string | null;
  updatedAt?: string | null;
  warnings?: LinkedInConfigurationFinding[];
}

function toConfiguration(body: ConfigurationBody): LinkedInConfiguration {
  return {
    id: body.id,
    status: body.status,
    agentMode: body.agentMode,
    suggestionsGenerated: body.suggestionsGenerated,
    ...asSections(body),
    createdAt: body.createdAt ?? null,
    updatedAt: body.updatedAt ?? null,
    warnings: body.warnings ?? [],
  };
}

/** A 404 is an answer here, not a failure: nothing has been saved, or there is no earlier version. */
function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** The company's configuration, or null when it has not saved one yet (LA-77). */
export async function fetchLinkedInConfiguration(): Promise<LinkedInConfiguration | null> {
  try {
    return toConfiguration(await apiClient.get<ConfigurationBody>(API.linkedinAgentConfiguration));
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

/**
 * Saves all four steps and completes the configuration (LA-78). A refused save is an `ApiError`
 * whose `fieldErrors` name every field of every step, and whose problem carries the warnings.
 */
export async function saveLinkedInConfiguration(
  request: LinkedInConfigurationRequest,
): Promise<LinkedInConfiguration> {
  return toConfiguration(
    await apiClient.put<ConfigurationBody>(API.linkedinAgentConfiguration, request),
  );
}

/** Autosaves the in-progress wizard, with no validation (LA-81). Refused 409 once complete. */
export async function saveLinkedInConfigurationDraft(
  request: LinkedInConfigurationRequest,
): Promise<LinkedInConfiguration> {
  return toConfiguration(
    await apiClient.patch<ConfigurationBody>(API.linkedinAgentConfigurationDraft, request),
  );
}

/** Changes a launched configuration, with the reason (LA-82). */
export async function editLinkedInConfiguration(
  request: LinkedInConfigurationEditRequest,
): Promise<LinkedInConfiguration> {
  return toConfiguration(
    await apiClient.patch<ConfigurationBody>(API.linkedinAgentConfigurationEdit, request),
  );
}

/** Picks the entry mode, or clears it with null (LA-83). */
export async function setLinkedInAgentMode(
  mode: LinkedInAgentMode | null,
): Promise<LinkedInConfiguration> {
  return toConfiguration(
    await apiClient.patch<ConfigurationBody>(API.linkedinAgentConfigurationMode, { mode }),
  );
}

/** Records that the AI suggestion step ran, so a reload does not run it again (LA-84). */
export async function markLinkedInSuggestionsGenerated(): Promise<LinkedInConfiguration> {
  return toConfiguration(
    await apiClient.patch<ConfigurationBody>(API.linkedinAgentConfigurationSuggestionsGenerated),
  );
}

interface HistoryBody {
  aboutYou: unknown;
  yourCustomers: unknown;
  yourMessaging: unknown;
  launchGoals: unknown;
  note?: string | null;
  createdAt?: string | null;
}

function toHistoryEntry(body: HistoryBody): LinkedInConfigurationHistoryEntry {
  return { ...asSections(body), note: body.note ?? null, createdAt: body.createdAt ?? null };
}

/** Every earlier version, newest first. Empty for a configuration never changed (LA-86). */
export async function fetchLinkedInConfigurationHistory(): Promise<
  LinkedInConfigurationHistoryEntry[]
> {
  const rows = await apiClient.get<HistoryBody[]>(API.linkedinAgentConfigurationHistory);
  return rows.map(toHistoryEntry);
}

/** The version before the last change, or null when there is none (LA-86). */
export async function fetchLatestLinkedInConfigurationHistory(): Promise<LinkedInConfigurationHistoryEntry | null> {
  try {
    return toHistoryEntry(
      await apiClient.get<HistoryBody>(API.linkedinAgentConfigurationHistoryLatest),
    );
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

interface ScrapedCompanyBody {
  companyUrl?: unknown;
  name?: unknown;
  scrapedAt?: unknown;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The companies already viewed, for the "Generate with AI" picker (LA-95). */
export async function fetchScrapedLinkedInCompanies(): Promise<ScrapedCompanySummary[]> {
  const rows = await apiClient.get<ScrapedCompanyBody[]>(API.linkedinAgentConfigurationCompanies);
  return rows.map((row) => ({
    companyUrl: text(row.companyUrl),
    name: text(row.name),
    scrapedAt: typeof row.scrapedAt === 'string' ? row.scrapedAt : null,
  }));
}

interface SuggestionBody {
  sections?: Partial<Record<keyof LinkedInConfigurationSections, unknown>>;
  company_name?: unknown;
  companyName?: unknown;
}

/** Suggested values for the four steps from one viewed company (LA-95). Nothing is stored. */
export async function generateLinkedInConfigurationSuggestions(
  companyUrl: string,
): Promise<GeneratedSuggestions> {
  const body = await apiClient.post<SuggestionBody>(
    API.linkedinAgentConfigurationSuggest,
    { companyUrl },
    { timeout: SUGGEST_TIMEOUT_MS },
  );
  const companyName = text(body.company_name) || text(body.companyName);
  return { sections: asSections(body.sections ?? {}), companyName: companyName || null };
}
